import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock, Zap, Star, Trophy, ChevronDown, ChevronUp, ChevronRight } from 'lucide-react';
import { ModuleId, SubSkillProgress } from '../../types';
import { useAppStore } from '../../hooks/useAppStore';
import { computeLevelProgress } from '../../utils/adaptiveEngine';
import { formatSkill, formatSubSkill } from '../../utils/formatters';
import { getLevelTier } from '../../config/levels';
import { computeSkillMasteries } from '../../utils/skillAggregation';
interface LevelLadderProps {
  moduleId: ModuleId;
  subSkills: Record<string, SubSkillProgress>;
}
export function LevelLadder({
  moduleId,
  subSkills
}: LevelLadderProps) {
  const {
    state,
    selectedGroup,
    getStartingLevel
  } = useAppStore();
  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);
  const [expandedSkillInLevel, setExpandedSkillInLevel] = useState<string | null>(null);
  // Compute level progress using the new engine helper
  const startingLevel = getStartingLevel(moduleId, selectedGroup);
  const progressInfo = computeLevelProgress(moduleId, selectedGroup, state.questionStates, startingLevel);
  const {
    levelDetails,
    fastTrackActive
  } = progressInfo;
  // Compute skill-level aggregated data for the expanded view
  const skillMasteries = computeSkillMasteries(moduleId, selectedGroup, state.questionStates);
  return <div className="w-full space-y-4" data-id="element-349">
      {/* Fast Track Badge */}
      {fastTrackActive && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3" data-id="element-350">
          <div className="bg-amber-100 p-2 rounded-full" data-id="element-351">
            <Zap className="w-4 h-4 text-amber-600 fill-amber-600" data-id="element-352" />
          </div>
          <div data-id="element-353">
            <p className="text-sm font-bold text-amber-800" data-id="element-354">
              Fast-Track Actief! ⚡
            </p>
            <p className="text-xs text-amber-600" data-id="element-355">
              Je gaat als een speer! We slaan makkelijke vragen over.
            </p>
          </div>
        </motion.div>}

      {/* Horizontal Ladder */}
      <div className="relative pt-6 pb-2 px-2 overflow-x-auto no-scrollbar" data-id="element-356">
        {/* Connecting Line */}
        <div className="absolute top-[48px] left-0 w-full h-1 bg-slate-100 -z-10" data-id="element-357" />

        <div className="flex justify-between items-start min-w-[300px] gap-2" data-id="element-358">
          {levelDetails.map((level, index) => {
          const isCompleted = level.isCompleted;
          const isCurrent = level.isCurrent;
          const isLocked = !level.isUnlocked;
          const isPlus = getLevelTier(level.levelId) === 'hard';
          const isExpanded = expandedLevel === level.levelId;
          // Determine status color
          let statusColor = 'bg-slate-100 border-slate-200 text-slate-400';
          if (isCompleted) statusColor = 'bg-emerald-100 border-emerald-500 text-emerald-600';else if (isCurrent) statusColor = 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-200';else if (!isLocked) statusColor = 'bg-white border-indigo-200 text-indigo-400';
          return <div key={level.levelId} className="flex flex-col items-center gap-2 relative group cursor-pointer" onClick={() => setExpandedLevel(isExpanded ? null : level.levelId)} data-id="element-359">
                {/* Node */}
                <motion.button whileHover={{
              scale: 1.1
            }} whileTap={{
              scale: 0.95
            }} className={`
                    w-11 h-11 rounded-full border-2 flex items-center justify-center z-10 transition-all
                    ${statusColor}
                    ${isCurrent ? 'ring-4 ring-indigo-100' : ''}
                    ${isExpanded ? 'ring-2 ring-indigo-400 ring-offset-2' : ''}
                  `} data-id="element-360">
                  {isCompleted ? <Check className="w-5 h-5" data-id="element-361" /> : isLocked ? <Lock className="w-4 h-4" data-id="element-362" /> : isCurrent ? <Star className="w-5 h-5 fill-current" data-id="element-363" /> : <span className="text-xs font-bold" data-id="element-364">{index + 1}</span>}
                </motion.button>

                {/* Label */}
                <div className="text-center" data-id="element-365">
                  <span className={`
                    text-xs font-bold block
                    ${isCurrent ? 'text-indigo-600' : isCompleted ? 'text-emerald-600' : 'text-slate-400'}
                  `} data-id="element-366">
                    {level.label}
                  </span>
                  {isPlus && <span className="text-[10px] text-amber-500 font-medium" data-id="element-367">
                      Plus
                    </span>}
                </div>

                {/* Current Level Indicator (Pulse) */}
                {isCurrent && <span className="absolute -top-1 right-0 flex h-3 w-3" data-id="element-368">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" data-id="element-369"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500" data-id="element-370"></span>
                  </span>}

                {/* Selection Indicator */}
                {isExpanded && <motion.div layoutId="selection-indicator" className="absolute -bottom-2 w-1.5 h-1.5 bg-indigo-500 rounded-full" data-id="element-371" />}
              </div>;
        })}
        </div>
      </div>

      {/* Expanded Level Details Card */}
      <AnimatePresence mode="wait" data-id="element-372">
        {expandedLevel !== null && <motion.div key={expandedLevel} initial={{
        opacity: 0,
        height: 0,
        y: -10
      }} animate={{
        opacity: 1,
        height: 'auto',
        y: 0
      }} exit={{
        opacity: 0,
        height: 0,
        y: -10
      }} className="overflow-hidden" data-id="element-373">
            {(() => {
          const level = levelDetails.find(l => l.levelId === expandedLevel);
          if (!level) return null;
          return <div className={`
                  rounded-2xl p-4 border-2 
                  ${level.isCurrent ? 'border-indigo-100 bg-indigo-50/50' : 'border-slate-100 bg-white'}
                `} data-id="element-374">
                  <div className="flex justify-between items-center mb-3" data-id="element-375">
                    <h3 className={`font-bold ${level.isCurrent ? 'text-indigo-900' : 'text-slate-900'}`} data-id="element-376">
                      {level.title}
                    </h3>
                    {level.isCompleted && <div className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1" data-id="element-377">
                        <Trophy className="w-3 h-3" data-id="element-378" />
                        Afgerond
                      </div>}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4" data-id="element-379">
                    <div className="flex justify-between text-xs mb-1.5 font-medium" data-id="element-380">
                      <span className="text-slate-500" data-id="element-381">Voortgang</span>
                      <span className="text-slate-900" data-id="element-382">
                        {level.practicedOrMasteredCount} /{' '}
                        {level.totalQuestions} doelen
                      </span>
                    </div>
                    <div className="h-2 bg-white rounded-full overflow-hidden border border-slate-100" data-id="element-383">
                      <motion.div initial={{
                  width: 0
                }} animate={{
                  width: `${level.progressPercent}%`
                }} className={`h-full rounded-full ${level.isCompleted ? 'bg-emerald-500' : 'bg-indigo-500'}`} data-id="element-384" />
                    </div>
                  </div>

                  {/* Skills Breakdown (aggregated from subSkills) */}
                  <div className="space-y-2" data-id="element-385">
                    {(() => {
                if (skillMasteries.length === 0 && !level.isCompleted) {
                  return <div className="text-xs text-slate-500 italic text-center py-2" data-id="element-386">
                            Start met oefenen om je vaardigheden te zien!
                          </div>;
                }
                return <div className="grid grid-cols-1 gap-2" data-id="element-387">
                          {skillMasteries.map(skill => {
                    const hasMultipleSubs = skill.subSkills.length > 1;
                    const isSkillExpanded = expandedSkillInLevel === skill.skill;
                    return <div key={skill.skill} className="overflow-hidden rounded-lg border border-slate-100" data-id="element-388">
                                <div className={`bg-white/80 p-2 flex items-center justify-between ${hasMultipleSubs ? 'cursor-pointer hover:bg-slate-50' : ''}`} onClick={() => {
                        if (hasMultipleSubs) {
                          setExpandedSkillInLevel(isSkillExpanded ? null : skill.skill);
                        }
                      }} data-id="element-389">
                                  <div className="flex items-center gap-2" data-id="element-390">
                                    {hasMultipleSubs && (isSkillExpanded ? <ChevronDown className="w-3 h-3 text-slate-400" data-id="element-391" /> : <ChevronRight className="w-3 h-3 text-slate-400" data-id="element-392" />)}
                                    <div className={`w-1.5 h-1.5 rounded-full ${skill.masteryScore >= 85 ? 'bg-emerald-500' : skill.masteryScore >= 60 ? 'bg-indigo-500' : 'bg-amber-400'}`} data-id="element-393" />
                                    <span className="text-xs font-medium text-slate-700 capitalize" data-id="element-394">
                                      {formatSkill(skill.skill)}
                                    </span>
                                  </div>
                                  <span className="text-xs font-bold text-slate-400" data-id="element-395">
                                    {skill.masteryScore}%
                                  </span>
                                </div>

                                {/* SubSkill drill-down */}
                                <AnimatePresence data-id="element-396">
                                  {isSkillExpanded && hasMultipleSubs && <motion.div initial={{
                          height: 0,
                          opacity: 0
                        }} animate={{
                          height: 'auto',
                          opacity: 1
                        }} exit={{
                          height: 0,
                          opacity: 0
                        }} transition={{
                          duration: 0.15
                        }} className="overflow-hidden" data-id="element-397">
                                      <div className="px-2 pb-2 pt-0.5 space-y-1 bg-slate-50/50 border-t border-slate-50" data-id="element-398">
                                        {skill.subSkills.sort((a, b) => a.masteryScore - b.masteryScore).map(sub => <div key={sub.subSkill} className="flex items-center justify-between px-2 py-1" data-id="element-399">
                                              <span className="text-[11px] text-slate-500 capitalize pl-5" data-id="element-400">
                                                {formatSubSkill(sub.subSkill)}
                                              </span>
                                              <span className="text-[11px] font-medium text-slate-400" data-id="element-401">
                                                {sub.masteryScore}%
                                              </span>
                                            </div>)}
                                      </div>
                                    </motion.div>}
                                </AnimatePresence>
                              </div>;
                  })}
                        </div>;
              })()}
                  </div>
                </div>;
        })()}
          </motion.div>}
      </AnimatePresence>
    </div>;
}